import React, { useState } from 'react'
import axios from 'axios'
import Modal from '../../shared/modal'

interface Props {
  onClose: () => void
  onCreated: () => void
}

interface ValidityPreset {
  label: string
  hours: number
}

const PRESETS: ValidityPreset[] = [
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
  { label: '14 days', hours: 336 }
]

const CUSTOM = 'custom'

const CreateVoucherModal: React.FC<Props> = ({ onClose, onCreated }) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [note, setNote] = useState('')
  const [sendSms, setSendSms] = useState(true)
  const [validity, setValidity] = useState<number | typeof CUSTOM>(
    PRESETS[2].hours
  ) // default 7 days
  const [customDate, setCustomDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    if (phoneNumber.trim() === '') {
      setError('Phone number is required.')
      return
    }
    if (validity === CUSTOM && customDate === '') {
      setError('Please pick a custom expiry date.')
      return
    }

    const payload: Record<string, string | number | boolean> = {
      phone_number: phoneNumber.trim(),
      note,
      send_sms: sendSms
    }
    if (validity === CUSTOM) {
      payload.expires_at = customDate
    } else {
      payload.validity_hours = validity
    }

    setSubmitting(true)
    setError(null)
    axios
      .post('/admin/vouchers', payload)
      .then(() => onCreated())
      .catch((err) => {
        setError(err.response?.data?.error || 'Could not create the voucher.')
        setSubmitting(false)
      })
  }

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={submit}
        disabled={submitting}
      >
        {submitting ? 'Creating…' : 'Create voucher'}
      </button>
    </>
  )

  return (
    <Modal
      headerText="Create voucher"
      onClose={onClose}
      footerContent={footer}
      centered
    >
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="form-group mb-3">
        <label htmlFor="voucher-phone">Customer phone number</label>
        <input
          id="voucher-phone"
          type="tel"
          className="form-control"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="Enter phone number"
        />
      </div>

      <div className="form-group mb-3">
        <label>Validity</label>
        <div
          className="voucher-validity-presets"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
        >
          {[
            ...PRESETS.map((p) => ({
              key: p.hours,
              label: p.label,
              value: p.hours as number | typeof CUSTOM
            })),
            {
              key: CUSTOM,
              label: 'Custom',
              value: CUSTOM as number | typeof CUSTOM
            }
          ].map((option) => {
            const selected = validity === option.value
            return (
              <button
                type="button"
                key={option.key}
                aria-pressed={selected}
                className={`btn ${selected ? 'btn-primary active' : 'btn-outline-secondary'}`}
                style={
                  selected
                    ? { boxShadow: '0 0 0 2px #0a58ca', fontWeight: 700 }
                    : undefined
                }
                onClick={() => setValidity(option.value)}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        {validity === CUSTOM && (
          <input
            type="date"
            className="form-control mt-2"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
          />
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="voucher-note">Note (optional)</label>
        <input
          id="voucher-note"
          type="text"
          className="form-control"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why is this voucher being granted?"
        />
      </div>

      <div className="form-check">
        <input
          id="voucher-send-sms"
          type="checkbox"
          className="form-check-input"
          checked={sendSms}
          onChange={(e) => setSendSms(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="voucher-send-sms">
          Text the voucher link to the customer now
        </label>
      </div>
    </Modal>
  )
}

export default CreateVoucherModal
